import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { connectorApi } from '@app/store';
import { IConnector, ConnectorDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { ConnectorForm } from './ConnectorForm';
import { useToast, useTriptych } from '@app/hooks';
import { ConnectorCard } from './ConnectorCard';

interface ConnectorUpdateProps {
  connector?: Partial<IConnector>;
  isFetching: boolean;
  toggle: (mode: CardButtonActionMode) => void;
}

export const transformToUpdatableFormConnector = (connector?: Partial<IConnector>) => {
  return {
    name: connector?.name,
    description: connector?.description,
    autoPublish: Boolean(connector?.autoPublish),
  }
}

export const ConnectorUpdate = ({ connector, isFetching, toggle }: ConnectorUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const [update, { isLoading, error: apiErrors }] = connectorApi.useUpdateOneConnectorsMutation();

  const formMethods = useForm({
    resolver: classValidatorResolver(ConnectorDTO),
    defaultValues: transformToUpdatableFormConnector(connector)
  });

  const onUpdate = async (formValues: Partial<IConnector>) => {
    try {
      const updatedConnector = await update({ _id: connector?._id!, ...formValues }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedConnector });

      toast.success('Votre connecteur a bien été modifié.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification de votre connecteur.')
    }
  }

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormConnector(connector));
  }, [connector, formMethods.reset]);

  return (
    <Fragment>
      <ConnectorCard
        connector={connector}
        formMethods={formMethods}
        toggle={toggle} />

      <Panel.Content>

        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>

          <Panel.FormContainer>

            <ConnectorForm />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};
